import { useState, useMemo, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import regulatoryUpdates from '../../../shared/data/regulatoryUpdates.json'

const CATEGORY_LABELS = {
  breach_notification: 'Breach Notification Change',
  penalty: 'Penalty Update',
  framework: 'Framework Update',
  authority: 'Authority Change',
  timeline: 'Timeline Change',
  new_regulation: 'New Regulation',
  ai_regulation: 'AI Regulation'
}

const AI_KEYWORDS = ['artificial intelligence', 'machine learning', 'algorithmic', 'ai governance', 'ai accountability', 'generative ai', 'large language model', 'automated decision', 'ai transparency', 'ai safety', 'ai risk', 'model risk']

// Derive a display label for the source. Privacy-feed entries carry a
// `source` short name directly (e.g. "IAPP", "Hunton"). Federal Register
// entries predate that field — fall back to a sensible default.
function getSourceLabel(finding) {
  if (finding.source) return finding.source
  if (finding.sourceType === 'federal-register') return 'Fed Register'
  if (finding.sourceType === 'privacy-feed') return 'Privacy Feed'
  return null
}

const SEVERITY_CONFIG = {
  high: {
    label: 'High',
    border: 'border-l-red-500',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    dot: 'bg-red-500'
  },
  medium: {
    label: 'Medium',
    border: 'border-l-amber-500',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    dot: 'bg-amber-500'
  },
  low: {
    label: 'Low',
    border: 'border-l-blue-500',
    badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    dot: 'bg-blue-500'
  }
}


function RegulatoryUpdates() {
  const [searchParams] = useSearchParams()
  const [severityFilter, setSeverityFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [industryFilter, setIndustryFilter] = useState('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchive, setShowArchive] = useState(false)
  const [aiOnly, setAiOnly] = useState(false)
  const [expandedId, setExpandedId] = useState(null)

  // Auto-expand a finding when navigating from a notification
  useEffect(() => {
    const expandFindingId = searchParams.get('expand')
    if (expandFindingId) {
      setExpandedId(expandFindingId)
      setTimeout(() => {
        const el = document.getElementById(`finding-${expandFindingId}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 200)
    }
  }, [searchParams])

  const ROLLING_WINDOW_DAYS = regulatoryUpdates?.rollingWindowDays || 60
  const cutoffDate = new Date(Date.now() - ROLLING_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString()

  // Active findings (within rolling window)
  const activeFindings = useMemo(() =>
    (regulatoryUpdates?.findings || []).filter(f => (f.publishedAt || f.detectedAt) >= cutoffDate),
    [cutoffDate]
  )
  const archivedFindings = regulatoryUpdates?.archivedFindings || []
  const allFindings = showArchive ? archivedFindings : activeFindings

  // Unique values for filters
  const categories = useMemo(() => [...new Set(allFindings.map(f => f.category))], [allFindings])
  const industries = useMemo(() => [...new Set(allFindings.flatMap(f => f.affectedIndustries || []))].sort(), [allFindings])
  const sources = useMemo(() => {
    const all = allFindings.map(getSourceLabel).filter(Boolean)
    return [...new Set(all)].sort()
  }, [allFindings])

  // Filter and sort. Resolved findings are always hidden.
  const filtered = useMemo(() => {
    let results = allFindings.filter(f => !f.resolved)

    if (severityFilter !== 'all') {
      results = results.filter(f => f.severity === severityFilter)
    }

    if (categoryFilter !== 'all') {
      results = results.filter(f => f.category === categoryFilter)
    }

    if (industryFilter !== 'all') {
      results = results.filter(f => (f.affectedIndustries || []).includes(industryFilter))
    }

    if (sourceFilter !== 'all') {
      results = results.filter(f => getSourceLabel(f) === sourceFilter)
    }

    if (aiOnly) {
      results = results.filter(f => {
        const text = `${f.title} ${f.summary || ''} ${f.keyTakeaway || ''}`.toLowerCase()
        return f.category === 'ai_regulation' || AI_KEYWORDS.some(kw => text.includes(kw))
      })
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter(f =>
        f.title.toLowerCase().includes(q) ||
        (f.summary || '').toLowerCase().includes(q) ||
        (f.keyTakeaway || '').toLowerCase().includes(q)
      )
    }

    return [...results].sort((a, b) => {
      const severityOrder = { high: 0, medium: 1, low: 2 }
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity]
      }
      return new Date(b.detectedAt) - new Date(a.detectedAt)
    })
  }, [allFindings, severityFilter, categoryFilter, industryFilter, sourceFilter, searchQuery, aiOnly])

  // Summary counts
  const counts = useMemo(() => {
    const active = allFindings.filter(f => !f.resolved)
    return {
      total: active.length,
      high: active.filter(f => f.severity === 'high').length,
      medium: active.filter(f => f.severity === 'medium').length,
      low: active.filter(f => f.severity === 'low').length
    }
  }, [allFindings])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-teal-50 dark:bg-teal-900/30">
              <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v18m0-18l-6 2l-3 9a5.002 5.002 0 006.001 0L6 5M12 3l6 2l3 9a5.002 5.002 0 01-6.001 0L18 5M8 21h8" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Regulatory Updates</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Last checked {new Date(regulatoryUpdates.lastChecked).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                {' · '}Next check {new Date(regulatoryUpdates.nextCheck).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Total Active</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{counts.total}</div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">High</span>
          </div>
          <div className="text-2xl font-bold text-red-600 dark:text-red-400">{counts.high}</div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Medium</span>
          </div>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{counts.medium}</div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Low</span>
          </div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{counts.low}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search updates..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Severity */}
          <select
            value={severityFilter}
            onChange={e => setSeverityFilter(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Severities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Category */}
          <select
            value={aiOnly ? 'ai_governance' : categoryFilter}
            onChange={e => {
              const val = e.target.value
              if (val === 'ai_governance') {
                setAiOnly(true)
                setCategoryFilter('all')
              } else {
                setAiOnly(false)
                setCategoryFilter(val)
              }
            }}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Categories</option>
            <option value="ai_governance">AI Governance</option>
            {categories.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>

          {/* Affected Industry */}
          <select
            value={industryFilter}
            onChange={e => setIndustryFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Verticals</option>
            {industries.map(i => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>

          {/* Source / Feed */}
          <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            className="pl-3 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Sources</option>
            {sources.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* View toggle: Active vs Archive */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setShowArchive(false)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showArchive ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            Active ({activeFindings.length})
          </button>
          <button
            onClick={() => setShowArchive(true)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showArchive ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
          >
            Archive ({archivedFindings.length})
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {showArchive ? 'Archived' : `Last ${ROLLING_WINDOW_DAYS} days`} · {filtered.length} update{filtered.length !== 1 ? 's' : ''}
        </p>
        {(severityFilter !== 'all' || categoryFilter !== 'all' || industryFilter !== 'all' || sourceFilter !== 'all' || searchQuery) && (
          <button
            onClick={() => { setSeverityFilter('all'); setCategoryFilter('all'); setIndustryFilter('all'); setSourceFilter('all'); setSearchQuery('') }}
            className="text-sm text-primary-600 dark:text-accent-300 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Findings list */}
      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">No regulatory updates match your filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((finding, idx) => {
            const config = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.low
            const isExpanded = expandedId === finding.id

            return (
              <div
                id={`finding-${finding.id}`}
                key={`${finding.id}-${idx}`}
                className={`card border-l-4 ${config.border} overflow-hidden`}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : finding.id)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${config.badge}`}>
                          {config.label}
                        </span>
                        <span className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {CATEGORY_LABELS[finding.category] || finding.category}
                        </span>
                        {(() => {
                          const sourceLabel = getSourceLabel(finding)
                          if (!sourceLabel) return null
                          // If the same item was reported by multiple feeds,
                          // append a +N suffix so the advisor knows it's
                          // multi-sourced (cross-confirmed news).
                          const extra = (finding.sources?.length || 1) - 1
                          return (
                            <span
                              className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400"
                              title={(finding.sources || [sourceLabel]).join(', ')}
                            >
                              {sourceLabel}{extra > 0 ? ` +${extra}` : ''}
                            </span>
                          )
                        })()}
                        {(finding.affectedIndustries || []).map(industry => (
                          <span key={industry} className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400">
                            {industry}
                          </span>
                        ))}
                        {finding.detectedAt && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {new Date(finding.detectedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{finding.title}</p>
                      {!isExpanded && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{finding.summary}</p>
                      )}
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Detail</h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{finding.summary}</p>
                    </div>
                    {finding.keyTakeaway && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Recommendation</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic">{finding.keyTakeaway}</p>
                      </div>
                    )}
                    {finding.platformAction && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Platform Action</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{finding.platformAction}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {finding.sourceUrl && (
                        <a
                          href={finding.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 dark:text-teal-400 hover:underline"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          View Source
                        </a>
                      )}
                      {finding.publishedAt && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Published: {new Date(finding.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default RegulatoryUpdates
